/**
 * usePatientSearch — loads Patient resources from Medplum for use in the
 * task create/edit drawer's "Related Client" selector.
 *
 * TODO: When volumes grow, switch to a server-side search with debouncing.
 */

import { useMedplum } from '@medplum/react-hooks';
import type { Patient } from '@medplum/fhirtypes';
import { useCallback, useEffect, useState } from 'react';
import { formatHumanName } from '@medplum/core';

export interface PatientOption {
  id: string;
  name: string;
  reference: string;
}

export interface UsePatientSearchResult {
  patients: PatientOption[];
  loading: boolean;
}

function patientToOption(p: Patient): PatientOption {
  const name = p.name?.[0] ? formatHumanName(p.name[0]) : `Patient/${p.id}`;
  return {
    id: p.id!,
    name,
    reference: `Patient/${p.id}`,
  };
}

export function usePatientSearch(): UsePatientSearchResult {
  const medplum = useMedplum();
  const [patients, setPatients] = useState<PatientOption[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      medplum.invalidateSearches('Patient');
      const results = await medplum.searchResources('Patient', {
        active: 'true',
        _count: '200',
        _sort: 'name',
      });
      setPatients(results.map(patientToOption));
    } catch {
      setPatients([]);
    } finally {
      setLoading(false);
    }
  }, [medplum]);

  useEffect(() => { load(); }, [load]);

  return { patients, loading };
}
